import React, { useEffect, useRef, useState } from 'react';
import TrodoWordmark from 'features/onboarding/components/TrodoWordmark';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTeam } from 'shared/context/teamContext';
import { fetchTeamActivation, markTeamActivatedLocal } from 'shared/utils/onboardingActivation';
import usePolling from 'shared/hooks/usePolling';

const PANEL_BG = 'var(--surface-sunken)';
const ACCENT = 'var(--trodo-primary)';
const POLL_MS = 3000;
const MAX_POLL_MS = 10000;

/**
 * Screen 2 of the onboarding install wall. The user has (hopefully) deployed
 * their instrumented code; we poll the team's activation state until the first
 * run lands, then drop them into the real app. Activation is authoritative and
 * server-side (teams.activated_at), so this screen just watches for the flip.
 */
const InstallDeployPage = () => {
  const navigate = useNavigate();
  const { team: teamParam } = useParams();
  const { selectedTeam, setSelectedTeam } = useTeam();

  const [elapsed, setElapsed] = useState(0);
  const doneRef = useRef(false);

  const teamId = selectedTeam?.id ?? selectedTeam?._id ?? null;
  const teamSlug = teamParam
    ? decodeURIComponent(teamParam)
    : selectedTeam?.name || '';

  // Check immediately (they may have already sent an event), then poll: 3s,
  // backing off to 10s while they deploy, and at once when they come back to
  // this tab from their terminal. Nothing while the tab is hidden.
  usePolling(async (isActive) => {
    const { activated } = await fetchTeamActivation(teamId);
    if (!isActive() || doneRef.current) return false;
    if (!activated) return true;
    doneRef.current = true;
    markTeamActivatedLocal(teamId);
    // Refresh the cached team so its `activated` flips true and the gate
    // won't bounce us back here, then enter the app.
    if (setSelectedTeam) {
      setSelectedTeam({ ...selectedTeam, activated: true });
    }
    navigate(`/${encodeURIComponent(teamSlug)}/chat`, { replace: true });
    return false;
  }, { enabled: teamId != null, interval: POLL_MS, maxInterval: MAX_POLL_MS, key: teamId });

  useEffect(() => {
    if (teamId == null) return undefined;
    const timer = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [teamId]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const elapsedLabel = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center px-6 py-10"
      style={{ fontFamily: 'Inter, system-ui, sans-serif', backgroundColor: PANEL_BG }}
    >
      <div className="w-full max-w-2xl">
        <TrodoWordmark className="mb-10" />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <h1 className="text-[2rem] leading-tight mb-3 text-[color:var(--text-primary)] font-normal tracking-tight">
            Deploy the code
          </h1>
          <p className="text-[15px] leading-relaxed text-[color:var(--text-muted)] mb-8">
            Push the code to your production or sandbox environment as you normally do, or run it
            locally. We'll tell you the moment we start receiving events from your code.
          </p>

          <div className="rounded-xl border border-[color:var(--border-default)] bg-surface px-5 py-6 flex items-center gap-4">
            <span
              className="inline-block h-3 w-3 rounded-full animate-pulse shrink-0"
              style={{ backgroundColor: ACCENT }}
            />
            <div className="flex-1">
              <div className="text-[15px] font-medium text-[color:var(--text-primary)]">
                Waiting for your first event…
              </div>
              <div className="text-[13px] text-[color:var(--text-muted)] mt-0.5">
                Listening for {elapsedLabel}. This page will move on automatically.
              </div>
            </div>
          </div>

          <div className="mt-9">
            <button
              type="button"
              onClick={() => navigate(`/${encodeURIComponent(teamSlug)}/install`)}
              className="text-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors"
            >
              ← Back to install
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InstallDeployPage;
